import { useCallback, useState } from 'react';
import StaffRewardImageUpload from './StaffRewardImageUpload';
import xmarkIcon from '../assets/icons/xmark.svg';

/** How many images one rung's gallery holds. The parent caps its own state to
 * the same number, so import this rather than repeating the literal. */
export const FIRST_REWARD_MAX_IMAGES = 6;

/**
 * Gallery editor for one first-reward rung.
 *
 * Wraps StaffRewardImageUpload — the same drag-and-drop surface the reward
 * experiences use — but uploads each dropped file to S3 immediately and keeps
 * only the returned URLs. Saved images render through `extraPreviewItems`, so
 * the previews stay visually identical to the reward form.
 *
 * Uploads run straight out of the setImages callback rather than from an
 * effect: the parent passes inline handlers, so an effect keyed on them would
 * re-run and cancel its own in-flight upload before the URL came back.
 */
const FirstRewardImagesField = ({
  urls,
  onAppend,
  onRemove,
  onUpload,
  inputId,
  maxImages = FIRST_REWARD_MAX_IMAGES,
}) => {
  const [uploading, setUploading] = useState(0);

  const handleFiles = useCallback(
    (updater) => {
      // StaffRewardImageUpload owns no persisted files here, so the updater is
      // always called with an empty list and returns just what was added.
      const added = typeof updater === 'function' ? updater([]) : updater;
      if (!added || !added.length) return;

      setUploading((n) => n + added.length);
      (async () => {
        for (const file of added) {
          try {
            const url = await onUpload(file);
            // Append one at a time so a slow batch fills in progressively and a
            // single failure doesn't discard the rest.
            if (url) onAppend([url]);
          } finally {
            setUploading((n) => Math.max(0, n - 1));
          }
        }
      })();
    },
    [onUpload, onAppend]
  );

  const remaining = Math.max(0, maxImages - urls.length);

  const savedPreviews = urls.map((url, index) => (
    <div key={`${url}-${index}`} className="image-preview-item">
      <div className="preview-image-wrapper">
        <img src={url} alt="" className="preview-image" />
        <button type="button" className="remove-image-btn" onClick={() => onRemove(index)}>
          <img src={xmarkIcon} alt="Remove" />
        </button>
      </div>
      <div className="preview-name">
        {index === 0 ? 'Image 1 (shown first)' : `Image ${index + 1}`}
      </div>
    </div>
  ));

  return (
    <>
      <StaffRewardImageUpload
        images={[]}
        setImages={handleFiles}
        inputId={inputId}
        maxImages={remaining}
        extraPreviewItems={savedPreviews.length ? savedPreviews : null}
      />
      {uploading > 0 && (
        <div
          style={{
            fontFamily: '"Baikal Book"',
            fontSize: '13px',
            color: '#6b7280',
            marginTop: '-8px',
            marginBottom: '12px',
          }}
        >
          Uploading {uploading} image{uploading === 1 ? '' : 's'}…
        </div>
      )}
    </>
  );
};

export default FirstRewardImagesField;
